import React from 'react';
import arrowIconSecondary from 'src/assets/svg/arrow-brand-secondary-500.svg';
import arrowIcon from 'src/assets/svg/arrow.svg';
import { useRefState } from 'src/shared/hooks/useRefState';
import { ifProp } from 'src/shared/styled-utils/ifProp';
import { prop } from 'src/shared/styled-utils/prop';
import { ZIndexes } from 'src/shared/ZIndexes';
import styled, { css } from 'styled-components';
import { Colors, Font, ScreenWidth, Spacing } from 'web-common/src/shared/styles';
import { useScrollSnapCarousel } from './useScrollSnapCarousel';

const Container = styled.div<{ centerContent: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: ${({ centerContent }) => (centerContent ? 'center' : 'start')};
  width: 100%;
`;

export const Items = styled.div<{ addLeftMargin: boolean; gapPixels?: number }>`
  display: flex;
  gap: ${({ gapPixels }) => (gapPixels ? `${gapPixels}px` : Spacing.SINGLE)};
  align-items: center;
  justify-content: flex-start;
  overflow-x: scroll;
  overflow-y: clip;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    -webkit-appearance: none;
    display: none;
  }

  scroll-snap-type: x mandatory;

  > div:first-child {
    margin-left: ${({ addLeftMargin }) => (addLeftMargin ? Spacing.DOUBLE : 'inherit')};
  }

  @media (max-width: ${ScreenWidth.SMALL}) {
    scroll-snap-type: none;
  }
`;

const CarouselGradient = styled.div<{ isVisible: boolean }>`
  position: absolute;
  z-index: ${ZIndexes.SegmentFiltersContainer + 1};
  height: 100%;
  width: 35px;
  transition: transform 0.1s ease-in-out;
  visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
`;

const Button = styled.button<{ hasSelected: boolean; isVisible: boolean }>`
  position: absolute;
  z-index: ${ZIndexes.SegmentFiltersContainer + 2};
  top: 50%;
  transform: translate(0, -50%);
  height: 40px;
  width: 40px;
  background: #f8f8f8;
  border-radius: 100%;
  ${ifProp(
    'hasSelected',
    css`
      background: ${Colors.GRAY_DARK_30};
      border-radius: 40px;
      width: 50px;
    `
  )}
  border: none;
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.1);
  visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: ${ScreenWidth.SMALL}) {
    display: none;
  }
`;

const NumSelected = styled.span`
  font-family: ${Font.Family.SANS};
  font-style: normal;
  font-weight: 600;
  font-size: ${Font.Size.XXS};
  line-height: ${Spacing.SINGLE};

  color: ${Colors.WHITE};

  margin: ${Spacing.NONE} ${Spacing.HALF};
`;

export const LeftCarouselGradient = styled(CarouselGradient)<{ isLightBackground: boolean }>`
  left: 0;
  background: ${ifProp(
    'isLightBackground',
    css`linear-gradient(270deg, rgba(255, 255, 255, 0) 0%, ${Colors.WHITE} 100%);`,
    css`linear-gradient(270deg, rgba(248, 248, 248, 0) 0%, #f8f8f8 100%);`
  )};
`;

export const RightCarouselGradient = styled(CarouselGradient)<{ isLightBackground: boolean }>`
  right: 0;
  background: ${ifProp(
    'isLightBackground',
    css`linear-gradient(270deg, ${Colors.WHITE} 0%, rgba(255, 255, 255, 0) 100%);`,
    css`linear-gradient(270deg, #f8f8f8 0%, rgba(248, 248, 248, 0) 100%);`
  )};
`;

const ArrowIcon = styled.img``;
ArrowIcon.defaultProps = {
  src: arrowIcon,
};

const ArrowIconSecondary = styled.img``;
ArrowIconSecondary.defaultProps = {
  src: arrowIconSecondary,
};

interface IInnerButtonProps {
  outerPadding?: string;
}

const LeftButton = styled(Button)<IInnerButtonProps>`
  left: ${prop('outerPadding', 0)};
`;

const RightButton = styled(Button)<IInnerButtonProps>`
  right: ${prop('outerPadding', 0)};

  & > ${ArrowIcon}, & > ${ArrowIconSecondary} {
    transform: rotate(180deg);
  }
`;

interface IButtonProps {
  direction: 'left' | 'right';
  isVisible: boolean;
  onClick: () => void;
  outerPadding?: string;
  numItemsSelectedOnLeft?: number;
  numItemsSelectedOnRight?: number;
  visibleOnSmallScreen?: boolean;
  isLightBackground?: boolean;
}

const ScrollButton: React.FC<IButtonProps> = ({
  direction,
  isVisible,
  onClick,
  outerPadding,
  numItemsSelectedOnLeft,
  numItemsSelectedOnRight,
}) =>
  direction === 'left' ? (
    <LeftButton
      onClick={onClick}
      aria-label='Scroll left'
      outerPadding={outerPadding}
      hasSelected={Boolean(numItemsSelectedOnLeft)}
      isVisible={isVisible}>
      {numItemsSelectedOnLeft ? <ArrowIconSecondary alt='Left arrow' /> : <ArrowIcon alt='Left arrow' />}
      {numItemsSelectedOnLeft ? <NumSelected>{numItemsSelectedOnLeft}</NumSelected> : null}
    </LeftButton>
  ) : (
    <RightButton
      onClick={onClick}
      aria-label='Scroll right'
      outerPadding={outerPadding}
      hasSelected={Boolean(numItemsSelectedOnRight)}
      isVisible={isVisible}>
      {numItemsSelectedOnRight ? <NumSelected>{numItemsSelectedOnRight}</NumSelected> : null}
      {numItemsSelectedOnRight ? <ArrowIconSecondary alt='Right arrow' /> : <ArrowIcon alt='Right arrow' />}
    </RightButton>
  );

interface IProps {
  gapPixels?: number;
  addLeftMargin?: boolean;
  outerButtonPadding?: string;
  showSelectedOffscreenCount?: boolean;
  onLeftScrollButtonClick?: () => void;
  onRightScrollButtonClick?: () => void;
  scrollSnapOnSmallScreen?: boolean;
  isLightBackground?: boolean;
  // Control whether a single item in a carousel is centered or left aligned on desktop
  centerContent?: boolean;
  showSideGradients?: boolean;
  hideScrollButtons?: boolean;
}

export const ScrollSnapCarousel: React.FC<React.PropsWithChildren<IProps>> = ({
  addLeftMargin = false,
  children,
  gapPixels,
  outerButtonPadding,
  showSelectedOffscreenCount,
  onLeftScrollButtonClick,
  onRightScrollButtonClick,
  scrollSnapOnSmallScreen = false,
  isLightBackground = false,
  centerContent = true,
  showSideGradients = true,
  hideScrollButtons = false,
  ...rest
}) => {
  const [ref, setRef] = useRefState<HTMLDivElement>();
  const { hasItemsOnLeft, hasItemsOnRight, scrollLeft, scrollRight, numItemsSelectedOnLeft, numItemsSelectedOnRight } =
    useScrollSnapCarousel(ref, scrollSnapOnSmallScreen);

  const handleLeftScrollButtonClick = () => {
    onLeftScrollButtonClick?.();
    scrollLeft();
  };

  const handleRightScrollButtonClick = () => {
    onRightScrollButtonClick?.();
    scrollRight();
  };

  // Ensure rest is an object, otherwise it will error since the iterable is null or undefined
  const safeRest = rest || {};

  return (
    <Container {...safeRest} centerContent={centerContent} id='container'>
      <Items gapPixels={gapPixels} addLeftMargin={addLeftMargin} id='Items' ref={setRef}>
        {children}
      </Items>
      {showSideGradients && <LeftCarouselGradient isVisible={hasItemsOnLeft} isLightBackground={isLightBackground} />}
      {showSideGradients && <RightCarouselGradient isVisible={hasItemsOnRight} isLightBackground={isLightBackground} />}
      {!hideScrollButtons && (
        <>
          <ScrollButton
            direction='left'
            isVisible={hasItemsOnLeft}
            onClick={handleLeftScrollButtonClick}
            outerPadding={outerButtonPadding}
            numItemsSelectedOnLeft={showSelectedOffscreenCount ? numItemsSelectedOnLeft : 0}
          />
          <ScrollButton
            direction='right'
            isVisible={hasItemsOnRight}
            onClick={handleRightScrollButtonClick}
            outerPadding={outerButtonPadding}
            numItemsSelectedOnRight={showSelectedOffscreenCount ? numItemsSelectedOnRight : 0}
          />
        </>
      )}
    </Container>
  );
};
